import { Box } from "@mui/system";
import { DataGridPro, GridColumnVisibilityModel, GridFilterModel, GridSortModel } from "@mui/x-data-grid-pro";
import { useMemo, useState, useCallback, useRef } from "react";
import { useAddScrapingMutation, useDisableKoBiddingManualMaxCpcMutation, useDisableScrapingMutation, useUpdateKoBiddingManualMutation } from "src/context/api/activations/api";
import { useCurrentWorkspace } from "src/context/reducers/app-settings";
import { useAuth } from "src/auth";
import ConfirmDisableModal from "src/sections/overview/dashboards/views/activations/keyword/modals/modal-confirm-disable";
import ModalScraping from "src/sections/overview/dashboards/views/activations/keyword/modals/modal-scraping";
import { FilterPanelCustom } from "../filter-panel-custom";
import ModalDeactivateStrategy from "src/sections/overview/dashboards/views/activations/keyword/modals/modal-deactivate-strategy";
import EnforceMaxCpcModal from "src/sections/overview/dashboards/views/activations/keyword/modals/modal-enforce-max-cpc";
import ModalCreateStrategyOther from "src/sections/overview/dashboards/views/activations/keyword/modals/modal-create-strategy-other";
import CustomToolbar from "./components/CustomToolbar";
import { useModals } from "./hooks/useModals";
import { gridStyles } from "./styles/grid";
import { useKeywordOtherColumns } from "./utils/useKeywordOtherColumns";
import { useActions } from "./hooks/data-grid-keyword/useActions";
import { dateRangeOptions, comparisonPeriodOptions } from "./constants/dateOptions";
import { useEnforceMaxCPCMutation } from "src/context/api/liveBidding/api";

interface Props {
    data: any[];
    rowCount: number;
    page: number;
    pageSize: number;
    onPageChange: (page: number) => void;
    onPageSizeChange: (pageSize: number) => void;
    sortModel: GridSortModel;
    setSortModel: (model: GridSortModel) => void;
    filterModel: GridFilterModel;
    setFilterModel: (model: GridFilterModel) => void;
    localPatches: Record<string, any>;
    setLocalPatches: React.Dispatch<React.SetStateAction<Record<string, any>>>;
    selectedDateRange?: { label: string; value: string };
    onDateRangeChange?: (newRange: { label: string; value: string }) => void;
    selectedCompareDate?: any;
    onCompareDateChange?: (newRange: { label: string; value: string }) => void;
    selectedCountry: any;
    onOpenCsvImport: () => void;
    refetch: any
}

export default function DataGridKeywordOther({
    data,
    rowCount,
    page,
    pageSize,
    onPageChange,
    onPageSizeChange,
    localPatches,
    setLocalPatches,
    sortModel,
    setSortModel,
    filterModel,
    setFilterModel,
    onDateRangeChange,
    onCompareDateChange,
    selectedCompareDate,
    selectedDateRange,
    onOpenCsvImport,
    refetch
}: Props) {
    const [selectionRows, setSelectionRows]: any = useState([])
    const workspace: any = useCurrentWorkspace();
    const lang = localStorage.getItem('i18nextLng') || 'en';
    const apiRef: any = useRef();
    const toolbarRef = useRef<HTMLDivElement>(null);
    const { user } = useAuth();

    const handleCloseScraping = useCallback(() => setModalScraping({ open: false, row: null }), []);

    const [enforceMaxCPC] = useEnforceMaxCPCMutation();
    const [addScraping] = useAddScrapingMutation();
    const [disableScraping] = useDisableScrapingMutation();
    const [disableKoBiddingManualMaxCpc] = useDisableKoBiddingManualMaxCpcMutation();
    const [updateMaxCpc] = useUpdateKoBiddingManualMutation();

    const [scrapingDetails, setScrapingDetails] = useState<any[]>([]);

    const { modalScraping, setModalScraping, modalCreateStrategy, setModalCreateStrategy, modalDeactivateStrategy,
        setModalDeactivateStrategy, confirmDisable, setConfirmDisable, openEnforceModal, setOpenEnforceModal,
    } = useModals();

    const [modifiedRows, setModifiedRows] = useState<{ id: string | number; max_cpc: number }[]>([]);

    const rowsToDisplay = useMemo(() => {
        const rowsArray = Array.isArray(modalScraping.row)
            ? modalScraping.row
            : modalScraping.row ? [modalScraping.row] : [];

        let result: any[] = [];
        for (const r of rowsArray) {
            const hasActive = r.scraping_details?.some((s: any) => s.status);
            if (hasActive) {
                result.push(...r.scraping_details.map((s: any, i: number) => ({
                    ...s,
                    id: s.id ?? `${r.id}-react-${i}`,
                    status: false,
                })));
            } else {
                result.push(...r.country_name.map((country: string, i: number) => ({
                    id: `${r.id}-new-${i}`,
                    keyword: r.keyword,
                    platform_scraping: ["Google"],
                    country,
                    frequency: "2 hours",
                    device: ["desktop", "mobile"],
                    status: true,
                    country_code: r.country_code,
                })));
            }
        }
        return result;
    }, [modalScraping]);

    const { handleConfirmDisable, handleToggle, handleUpdateMaxCPC, handleFinish, handleRowUpdate, handleConfirmScraping } = useActions({
        workspace,
        confirmDisable,
        modalScraping,
        rowsToDisplay,
        enforceMaxCPC,
        handleCloseScraping,
        updateMaxCpc,
        addScraping,
        scrapingDetails,
        disableScraping,
        disableKoBiddingManualMaxCpc,
        setLocalPatches,
        setModifiedRows,
        setModalScraping,
        setConfirmDisable
    });

    const mergedRows = useMemo(() => {
        return data.map((row) => {
            const patch = localPatches[row.id];

            if (!patch) return row;

            return {
                ...row,
                ...patch,
                activations_details: patch.activations_details ?? row.activations_details,
            };
        });
    }, [data, localPatches]);

    const columns = useKeywordOtherColumns({
        workspace,
        lang,
        handleToggle,
        selectedCompareDate,
        setConfirmDisable,
        setModalCreateStrategy,
    });

    const [columnVisibilityModel, setColumnVisibilityModel] = useState<GridColumnVisibilityModel>(() => {
        const saved = localStorage.getItem("columnVisibilityModelActivationsOther");
        return saved ? JSON.parse(saved) : {
            campaign_names: false,
            campaign_ids: false,
            ad_group_names: false,
            ad_group_ids: false,
            keyword_id: false,
            region_name: false,
            cost_last30d: false,
            platform: false,
            match_type: true,
        };
    });

    const handleColumnVisibilityChange = useCallback((newModel: GridColumnVisibilityModel) => {
        setColumnVisibilityModel(newModel);
        localStorage.setItem("columnVisibilityModelActivationsOther", JSON.stringify(newModel));
    }, []);

    return (
        <Box sx={gridStyles}>
            <DataGridPro
                rowHeight={57}
                processRowUpdate={handleRowUpdate}
                apiRef={apiRef}
                getRowId={(r) => r.id}
                rows={mergedRows}
                rowCount={rowCount}
                paginationModel={{ page, pageSize }}
                pageSizeOptions={[10, 25, 50, 100, 200]}
                columnVisibilityModel={columnVisibilityModel}
                onColumnVisibilityModelChange={handleColumnVisibilityChange}
                onPaginationModelChange={({ page, pageSize }) => {
                    onPageChange(page);
                    onPageSizeChange(pageSize);
                }}
                columnHeaderHeight={50}

                pagination
                paginationMode="server"
                sortingMode="server"
                sortModel={sortModel}
                onSortModelChange={setSortModel}
                keepNonExistentRowsSelected
                filterMode="server"
                filterModel={filterModel}
                onFilterModelChange={setFilterModel}
                slots={{
                    toolbar: () => <CustomToolbar
                        toolbarRef={toolbarRef}
                        apiRef={apiRef}
                        selectionRows={selectionRows}
                        canImport={["admin", "brand specialist"].includes(user?.role ?? "")}
                        onOpenCsvImport={onOpenCsvImport}
                        selectedDateRange={selectedDateRange}
                        selectedCompareDate={selectedCompareDate}
                        onDateRangeChange={onDateRangeChange}
                        onCompareDateChange={onCompareDateChange}
                        modifiedRows={modifiedRows}
                        setModifiedRows={setModifiedRows}
                        setLocalPatches={setLocalPatches}
                        handleUpdateMaxCPC={handleUpdateMaxCPC}
                        setModalDeactivateStrategy={setModalDeactivateStrategy}
                        setModalScraping={setModalScraping}
                        setModalCreateStrategy={setModalCreateStrategy}
                        setOpenEnforceModal={setOpenEnforceModal}
                        dateRangeOptions={dateRangeOptions}
                        comparisonPeriodOptions={comparisonPeriodOptions}
                    />,
                    filterPanel: (props) => <FilterPanelCustom {...props} getColumnForNewFilter={(args) => args ? args.columns[0].field : undefined} />,
                }}
                checkboxSelection
                onRowSelectionModelChange={(newSelection: any) => {
                    const selectedIds = Array.isArray(newSelection) ? newSelection : [];
                    setSelectionRows(
                        mergedRows.filter((row) => selectedIds.includes(row.id))
                    );
                }}
                getRowClassName={(params) =>
                    [
                        params.row.isEdited ? "edited-row" : "",
                        params.row.isInvalid ? "row-invalid" : ""
                    ].join(" ")
                }
                isCellEditable={(params) => {
                    if (params.field !== "max_cpc") return false;

                    const activations = params.row.activations_details || [];
                    const koManual = activations.find(
                        (d: any) => d.status === true && d.mode === "KO Bidding Manual"
                    );

                    return Boolean(koManual);
                }}
                disableRowSelectionOnClick
                columns={columns}
            />

            <ModalScraping
                open={modalScraping.open}
                rowsToDisplay={rowsToDisplay}
                onClose={handleCloseScraping}
                onConfirm={handleConfirmScraping}
                setScrapingDetails={setScrapingDetails}
            />

            <ModalCreateStrategyOther
                open={modalCreateStrategy.open}
                row={modalCreateStrategy.row}
                onClose={() => setModalCreateStrategy({ open: false, row: null })}
                setLocalPatches={setLocalPatches}
            />

            <ModalDeactivateStrategy
                refetch={refetch}
                open={modalDeactivateStrategy.open}
                row={modalDeactivateStrategy.row}
                onClose={() => setModalDeactivateStrategy({ open: false, row: null })}
                setLocalPatches={setLocalPatches}
            />

            <ConfirmDisableModal
                open={confirmDisable.open}
                row={confirmDisable.row}
                onClose={() => setConfirmDisable({ open: false, row: null, mode: null })}
                onConfirm={handleConfirmDisable}
            />

            <EnforceMaxCpcModal
                onFinish={handleFinish}
                open={openEnforceModal.open}
                onClose={() => setOpenEnforceModal({ open: false, row: null })}
                selectedRows={openEnforceModal.row}
                workspaceId={workspace?.id}
            />
        </Box>
    );
}